import { useEffect, useMemo, useRef, useState } from 'react'
import { feature } from 'topojson-client'
import { geoPath } from 'd3-geo'
import { Table2, Globe2 } from 'lucide-react'
import world from '@/assets/world.topojson'
import { orthographic, projectLatLon } from '@/lib/geo'
import type { Actor, Incident, Severity, Target, ThreatMapData } from '@/types/map'
import { AttackArc, UnattribBurst } from './icons/MapIcons'
import { GeoLayerLegend } from './GeoLayerLegend'
import { incidentAttributionLabel, incidentProjectionSummary } from './map-display-utils'
import { ProvenanceMiniLine, ReasonChips } from './ProjectionProvenance'

/**
 * SP6 Globe view.
 *
 * Renders an orthographic SVG projection of the world with severity-coloured
 * incident markers, attack-path arcs for attributed incidents, and an
 * UNATTRIB burst glyph for attacks with no known origin.
 *
 * Auto-rotation runs at 0.2°/s, pauses for 10s after any pointer interaction,
 * and is completely disabled when `prefers-reduced-motion: reduce` matches.
 *
 * All colours are resolved from `--severity-*` / `--surface-*` /
 * `--text-tertiary` CSS custom properties; nothing in this component hardcodes
 * a hex or rgb value (DESIGN.md compliance).
 */
export interface GlobeProps {
  data: ThreatMapData
  onDrillCountry: (iso2: string) => void
  isLoading: boolean
  error: unknown
}

const WIDTH = 800
const HEIGHT = 800
const INTERACTION_IDLE_MS = 10_000
const ROTATION_DEG_PER_SEC = 0.2
const NEW_INCIDENT_THRESHOLD_MS = 5_000

export function Globe({ data, onDrillCountry, isLoading, error }: GlobeProps) {
  const [rotation, setRotation] = useState<[number, number]>([0, -15])
  // Rotation-enabled flag; `data-rotating` exposes it for a11y / tests.
  // Honour `prefers-reduced-motion` at mount time.
  const [rotating, setRotating] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })
  const lastInteractionRef = useRef<number>(0)

  // Keep the rotating flag in sync with future reduced-motion changes so a
  // user toggling the OS setting mid-session gets the right behaviour.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setRotating(!mq.matches)
    mq.addEventListener?.('change', onChange)
    return () => mq.removeEventListener?.('change', onChange)
  }, [])

  // Auto-rotation raf loop — gated by `rotating` and paused for 10s after
  // any pointer interaction (tracked via lastInteractionRef).
  useEffect(() => {
    if (!rotating) return
    let raf = 0
    let last = performance.now()
    const loop = (now: number) => {
      const sinceInteraction = Date.now() - lastInteractionRef.current
      if (sinceInteraction < INTERACTION_IDLE_MS) {
        last = now
        raf = requestAnimationFrame(loop)
        return
      }
      const dt = (now - last) / 1000
      last = now
      setRotation(([lon, lat]) => {
        const next = lon + ROTATION_DEG_PER_SEC * dt * 60
        // Normalise so the value doesn't grow unbounded over long sessions.
        return [((next + 540) % 360) - 180, lat]
      })
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [rotating])

  // Show skeleton only on first load (no data yet). Subsequent refetches keep
  // the globe visible even if isLoading=true.
  if (isLoading && data.incidents.length === 0) {
    return <GlobeSkeleton />
  }
  if (error) {
    return (
      <div className="p-4 text-[color:var(--severity-high)]" role="alert">
        Failed to load globe data.
      </div>
    )
  }

  const proj = orthographic({ width: WIDTH, height: HEIGHT, rotation })
  const path = geoPath(proj)
  const topology = world as unknown as {
    objects: { land?: unknown; countries: unknown }
  }
  const landObj = (topology.objects.land ?? topology.objects.countries) as Parameters<
    typeof feature
  >[1]
  const countriesObj = topology.objects.countries as Parameters<typeof feature>[1]
  // `feature` returns a GeoJSON FeatureCollection or Feature depending on the
  // input. We normalise to a FeatureCollection with a `features` array for
  // the per-country borders pass, and keep the raw result for the land fill.
  const landFeature = feature(world as never, landObj) as unknown
  const countriesFc = feature(world as never, countriesObj) as {
    features?: unknown[]
  }
  const countries = Array.isArray(countriesFc.features) ? countriesFc.features : []

  const hasIncidents = data.incidents.length > 0
  const now = Date.now()

  const onPointerDown = () => {
    lastInteractionRef.current = Date.now()
  }

  return (
    <GlobeFrame data={data} hasIncidents={hasIncidents}>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-label="Globe view"
        data-rotating={rotating}
        preserveAspectRatio="xMidYMid meet"
        className="block h-full w-full"
        onPointerDown={onPointerDown}
      >
        {/* Atmosphere halo — decorative SOC depth cue, additive only. */}
        <defs>
          <radialGradient id="globe-halo">
            <stop offset="80%" stopColor="var(--interactive-primary)" stopOpacity={0} />
            <stop offset="93%" stopColor="var(--interactive-primary)" stopOpacity={0.22} />
            <stop offset="100%" stopColor="var(--interactive-primary)" stopOpacity={0} />
          </radialGradient>
        </defs>
        <circle
          cx={WIDTH / 2}
          cy={HEIGHT / 2}
          r={Math.min(WIDTH, HEIGHT) / 2}
          fill="url(#globe-halo)"
          aria-hidden
        />
        {/* Ocean disc */}
        <circle
          cx={WIDTH / 2}
          cy={HEIGHT / 2}
          r={Math.min(WIDTH, HEIGHT) / 2.1}
          fill="var(--surface-secondary)"
        />
        {/* Land fill */}
        <path
          d={path(landFeature as never) ?? ''}
          fill="var(--surface-tertiary)"
          stroke="var(--border-subtle)"
          strokeWidth={0.5}
        />
        {/* Per-country borders */}
        {countries.map((c, i) => (
          <path
            key={`country-${i}`}
            d={path(c as never) ?? ''}
            fill="none"
            stroke="var(--border-subtle)"
            strokeWidth={0.4}
          />
        ))}
        {/* Attack-path arcs (below markers). */}
        {data.incidents.map((inc) =>
          renderArc(inc, data.actors, data.targets, proj, now),
        )}
        {/* Incident markers (above arcs) — attributed + unattributed. */}
        {data.incidents.map((inc) =>
          renderMarker(
            inc,
            data.actors,
            data.targets,
            proj,
            now,
            onDrillCountry,
            countVolumeAt(inc, data.incidents),
          ),
        )}
      </svg>
    </GlobeFrame>
  )
}

/**
 * Centred + aspect-ratio-locked wrapper around the Globe SVG.
 *
 * The prior wrapper was `h-full w-full`, which let the SVG paint anywhere the
 * `<main>` container gave it. On widescreen viewports the column was wider
 * than it was tall, so the orthographic disc anchored to the top-left and the
 * globe visually hugged the left/top edges of the map column.
 *
 * This wrapper applies three corrections:
 *   1. Flex-centres the SVG horizontally + vertically in its parent.
 *   2. Constrains the SVG box to `aspect-square` so `preserveAspectRatio`
 *      can't produce a non-square render box.
 *   3. Caps the SVG box at ~90% of the smaller parent dimension so there's
 *      always breathing room around the globe.
 *
 * Layered on top (absolute-positioned, non-interactive) are the legend and
 * the "Show as table" toggle, per DESIGN.md §Geographic Map.
 */
function GlobeFrame({
  data,
  hasIncidents,
  children,
}: {
  data: ThreatMapData
  hasIncidents: boolean
  children: React.ReactNode
}) {
  const [tableView, setTableView] = useState(false)
  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
      {!hasIncidents && !tableView && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-sm text-[color:var(--text-tertiary)]">
          No incidents in the last 24 hours
        </div>
      )}
      {tableView ? (
        <IncidentTable data={data} />
      ) : (
        <div
          className="relative"
          style={{
            aspectRatio: '1 / 1',
            width: 'min(90%, calc(90vh - 8rem))',
            maxHeight: '90%',
          }}
          data-testid="globe-frame"
        >
          {children}
        </div>
      )}
      <GlobeLegend incidents={data.incidents} geoLayers={data.geoLayers} />
      <button
        type="button"
        onClick={() => setTableView((v) => !v)}
        data-testid="globe-view-toggle"
        aria-label={tableView ? 'Show globe view' : 'Show as table'}
        className="absolute right-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)]/90 px-2.5 py-1 text-xs font-medium text-[color:var(--text-secondary)] backdrop-blur hover:bg-[color:var(--surface-tertiary)]"
      >
        {tableView ? (
          <>
            <Globe2 className="h-3.5 w-3.5" aria-hidden />
            Globe
          </>
        ) : (
          <>
            <Table2 className="h-3.5 w-3.5" aria-hidden />
            Show as table
          </>
        )}
      </button>
    </div>
  )
}

/** Severity + volume legend, bottom-left of the map frame. */
function GlobeLegend({
  incidents,
  geoLayers,
}: {
  incidents: Incident[]
  geoLayers?: ThreatMapData['geoLayers']
}) {
  const severities: Severity[] = ['critical', 'high', 'medium', 'low']
  const counts = useMemo(() => {
    const by: Record<Severity, number> = { critical: 0, high: 0, medium: 0, low: 0 }
    for (const inc of incidents) by[inc.severity] = (by[inc.severity] ?? 0) + 1
    return by
  }, [incidents])
  return (
    <div
      data-testid="globe-legend"
      className="pointer-events-none absolute bottom-3 left-3 z-20 flex flex-col gap-1.5 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)]/90 p-2 text-[10px] font-mono uppercase tracking-wide text-[color:var(--text-tertiary)] backdrop-blur"
    >
      <div className="flex items-center gap-2">
        {severities.map((s) => (
          <span key={s} className="flex items-center gap-1">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: `var(--severity-${s})`, opacity: 0.8 }}
            />
            <span>
              {s[0].toUpperCase()}: {counts[s]}
            </span>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-1.5 text-[9px]">
        <span>volume</span>
        <span
          className="inline-block rounded-full"
          style={{
            width: 8,
            height: 8,
            backgroundColor: 'var(--severity-medium)',
            opacity: 0.25,
          }}
        />
        <span
          className="inline-block rounded-full"
          style={{
            width: 14,
            height: 14,
            backgroundColor: 'var(--severity-medium)',
            opacity: 0.25,
          }}
        />
        <span
          className="inline-block rounded-full"
          style={{
            width: 20,
            height: 20,
            backgroundColor: 'var(--severity-medium)',
            opacity: 0.25,
          }}
        />
      </div>
      <GeoLayerLegend layers={geoLayers} />
    </div>
  )
}

/** Tabular fallback view of the same incident dataset (DESIGN.md mandatory). */
function IncidentTable({ data }: { data: ThreatMapData }) {
  const rows = data.incidents.slice(0, 60)
  return (
    <div
      data-testid="globe-table-view"
      className="relative flex h-full w-full flex-col overflow-hidden"
    >
      <div className="shrink-0 border-b border-[color:var(--border-subtle)] px-3 py-2 text-[10px] font-mono uppercase tracking-wide text-[color:var(--text-tertiary)]">
        Incidents · tabular view · {data.incidents.length} total
      </div>
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[color:var(--surface-secondary)] text-[color:var(--text-tertiary)]">
            <tr>
              <th className="px-3 py-1.5 text-left font-mono text-[10px] uppercase">Severity</th>
              <th className="px-3 py-1.5 text-left font-mono text-[10px] uppercase">Target</th>
              <th className="px-3 py-1.5 text-left font-mono text-[10px] uppercase">Actor</th>
              <th className="px-3 py-1.5 text-left font-mono text-[10px] uppercase">Country</th>
              <th className="px-3 py-1.5 text-left font-mono text-[10px] uppercase">Projection</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((inc) => {
              const target = data.targets.find((t) => t.id === inc.targetId)
              const actor = data.actors.find((a) => a.id === inc.actorId)
              const actorLabel = incidentAttributionLabel(inc, actor?.name)
              return (
                <tr
                  key={inc.id}
                  className="border-b border-[color:var(--border-subtle)]/60"
                >
                  <td className="px-3 py-1">
                    <span
                      className="inline-block h-2 w-2 rounded-full align-middle"
                      style={{ backgroundColor: `var(--severity-${inc.severity})` }}
                    />{' '}
                    <span className="font-mono uppercase text-[10px]">
                      {inc.severity}
                    </span>
                  </td>
                  <td className="truncate px-3 py-1 text-[color:var(--text-primary)]">
                    {target?.name ?? '—'}
                  </td>
                  <td className="truncate px-3 py-1 text-[color:var(--text-secondary)]">
                    {actorLabel}
                  </td>
                  <td className="px-3 py-1 font-mono text-[10px] text-[color:var(--text-tertiary)]">
                    {target?.countryIso2 ?? '—'}
                  </td>
                  <td className="px-3 py-1 text-[color:var(--text-tertiary)]">
                    <div className="flex min-w-44 flex-col gap-1">
                      <ReasonChips reasons={inc.unknownReasons} max={2} label={`Reasons for ${inc.id}`} />
                      {inc.provenance && inc.provenance.length > 0 && (
                        <span className="truncate font-mono text-[9px] uppercase tracking-[0.12em]">
                          <ProvenanceMiniLine provenance={inc.provenance} />
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function renderArc(
  inc: Incident,
  actors: Actor[],
  targets: Target[],
  proj: ReturnType<typeof orthographic>,
  now: number,
) {
  const actor = actors.find((a) => a.id === inc.actorId)
  const target = targets.find((t) => t.id === inc.targetId)
  if (!target) return null
  if (!actor?.originCountryIso2) return null // unattributed: no arc
  const ori = originCentroid(actor.originCountryIso2)
  if (!ori) return null
  const dst = projectLatLon(proj, target.latitude, target.longitude)
  const src = projectLatLon(proj, ori.lat, ori.lon)
  if (!dst || !src) return null
  const isNew = now - inc.startedAt < NEW_INCIDENT_THRESHOLD_MS
  return (
    <AttackArc
      key={`arc-${inc.id}`}
      src={src}
      dst={dst}
      severity={inc.severity}
      isNew={isNew}
    />
  )
}

function renderMarker(
  inc: Incident,
  actors: Actor[],
  targets: Target[],
  proj: ReturnType<typeof orthographic>,
  now: number,
  onDrillCountry: (iso2: string) => void,
  volume: number,
) {
  const target = targets.find((t) => t.id === inc.targetId)
  if (!target) return null
  const p = projectLatLon(proj, target.latitude, target.longitude)
  if (!p) return null
  const actor = actors.find((a) => a.id === inc.actorId)
  const isAttributed = !!actor?.originCountryIso2
  const isNew = now - inc.startedAt < NEW_INCIDENT_THRESHOLD_MS
  const actorLabel = incidentAttributionLabel(inc, actor?.name)
  const projectionSummary = incidentProjectionSummary(inc)

  // DESIGN.md §Geographic Map: centre dot 6–10px solid at 80% opacity,
  // halo at 25% opacity with radius encoding volume (incident count at
  // this country). Halo grows from 3× to 7× the centre radius as volume
  // ramps from 1 → 8+.
  const centreR = 4 // 8px dot
  const haloScale = Math.min(3 + Math.log2(volume), 7)
  const haloR = centreR * haloScale
  const fill = `var(--severity-${inc.severity})`

  return (
    <g
      key={`marker-${inc.id}`}
      data-testid="incident-marker"
      data-volume={volume}
      // R28: names the TARGET ORGANISATION in an attribute.
      data-ph-no-capture
      aria-label={`${actorLabel} targeting ${target.name}${projectionSummary ? ` — ${projectionSummary}` : ''}`}
      onClick={() => onDrillCountry(target.countryIso2)}
      style={{ cursor: 'pointer' }}
    >
      <title>
        {actorLabel} → {target.name}
        {projectionSummary ? ` · ${projectionSummary}` : ''}
      </title>
      {isAttributed ? (
        <>
          {isNew && (
            <circle
              data-testid="incident-pulse-ring"
              cx={p[0]}
              cy={p[1]}
              r={4}
              fill="none"
              stroke={fill}
              strokeWidth={1.5}
            >
              <animate
                attributeName="r"
                from={4}
                to={24}
                dur="1.5s"
                fill="freeze"
                repeatCount={1}
              />
              <animate
                attributeName="opacity"
                from={0.9}
                to={0}
                dur="1.5s"
                fill="freeze"
                repeatCount={1}
              />
            </circle>
          )}
          {/* Graduated hotspot: halo (volume-encoded, 25%) + centre dot (80%). */}
          <circle
            data-testid="hotspot-halo"
            cx={p[0]}
            cy={p[1]}
            r={haloR}
            fill={fill}
            fillOpacity={0.25}
          />
          <circle
            data-testid="hotspot-centre"
            cx={p[0]}
            cy={p[1]}
            r={centreR}
            fill={fill}
            fillOpacity={0.8}
          />
        </>
      ) : (
        <UnattribBurst cx={p[0]} cy={p[1]} severity={inc.severity} />
      )}
    </g>
  )
}

/**
 * Count incidents at the same target country as `inc` (inclusive). Used as
 * the volume signal for the graduated-hotspot halo radius.
 */
function countVolumeAt(inc: Incident, all: Incident[]): number {
  // The hook's Incident type doesn't carry targetCountry directly, so group
  // by targetId as a proxy. When two incidents target the same site, they
  // boost each other's halo.
  let c = 0
  for (const o of all) if (o.targetId === inc.targetId) c++
  return Math.max(1, c)
}

function GlobeSkeleton() {
  return (
    <div
      data-testid="globe-skeleton"
      className="h-full w-full animate-pulse bg-[color:var(--surface-secondary)]"
    />
  )
}

/**
 * Coarse origin-country centroid lookup for arc endpoints. A full country
 * centroid table would be overkill for an arc-anchor; the Globe only needs a
 * point inside the right landmass. When an actor's origin ISO2 isn't in this
 * table, we skip the arc rather than guess.
 */
function originCentroid(iso2: string): { lat: number; lon: number } | null {
  const CENTROIDS: Record<string, [number, number]> = {
    RU: [55.75, 37.6],
    US: [39.5, -98.5],
    CN: [35, 103],
    IR: [32.5, 54],
    KP: [40, 127],
    GB: [54, -2],
    DE: [51.1, 10.4],
    FR: [46.2, 2.2],
    UA: [49, 32],
    BY: [53.7, 27.9],
    IL: [31.5, 34.8],
    IN: [22, 79],
    PK: [30, 70],
    BR: [-10, -52],
    JP: [36, 138],
    KR: [36.5, 127.5],
  }
  const v = CENTROIDS[iso2]
  return v ? { lat: v[0], lon: v[1] } : null
}
